/**
 * Home hero — the verb-first "Do now" queue + plain-language ops health.
 *
 * Two pure summarizers behind the Dashboard's hero:
 *
 * 1. `buildDoNowQueue` — takes the ranked Autopilot ops queue
 *    (`assembleWeeklyOps`) and returns the top few items dressed for action:
 *    each with a verb-first CTA label, a real view to deep-link to, and a
 *    ready-made "hand it to the agent" query. It invents nothing — every
 *    entry is a `WeeklyOpsItem` the ranking engine already justified.
 *
 * 2. `summarizeAqi` — translates the bare AQI number ("AQI 29"?) into plain
 *    language: what the score means and the single most effective thing to
 *    do about it (the weakest component, named with its receipt).
 *
 * PURE by contract: no store, no network, no clock, no input mutation.
 */

import type { WeeklyOpsItem, WeeklyOpsKind } from './weeklyOps';
import type { AqiComponent, AqiResult } from './aqi';
import { aqiBand } from './aqi';

// ---------------------------------------------------------------------------
// 1 — The "Do now" queue
// ---------------------------------------------------------------------------

/** Views a Do-now CTA may deep-link to — a subset of the app's ViewKey. */
export type DoNowTarget = 'contacts' | 'customer-success' | 'tickets';

/** One hero row: a ranked ops item dressed with its one-click action. */
export interface DoNowAction {
  /** The underlying ranked ops-queue item (id, title, reason, score). */
  item: WeeklyOpsItem;
  /** Verb-first CTA label, e.g. "Follow up" / "Check in". */
  actionLabel: string;
  /** The real view the CTA deep-links to. */
  targetView: DoNowTarget;
  /** Ready-made query for the chat agent ("let Argo handle it"). */
  askQuery: string;
}

/** Default hero size: enough to act on, small enough to actually read. */
export const DO_NOW_LIMIT = 5;

/** Per-kind CTA: the verb + the view that owns the underlying record. */
const KIND_CTA: Record<WeeklyOpsKind, { label: string; view: DoNowTarget }> = {
  at_risk: { label: 'Check in', view: 'customer-success' },
  stale_finding: { label: 'Triage', view: 'tickets' },
  overdue_deliverable: { label: 'Open deliverables', view: 'customer-success' },
  overdue_followup: { label: 'Follow up', view: 'contacts' },
  weekly_update: { label: 'Draft update', view: 'customer-success' },
};

/**
 * The top `limit` ops-queue items, each with its one-click action. Re-sorts
 * defensively (highest `priorityScore` first, same tie-breaks as the
 * engine) so callers can pass any WeeklyOpsItem[] without pre-sorting.
 * Empty input → empty queue; never fabricates work.
 */
export function buildDoNowQueue(
  items: WeeklyOpsItem[],
  limit: number = DO_NOW_LIMIT,
): DoNowAction[] {
  return [...items]
    .sort(
      (a, b) =>
        b.priorityScore - a.priorityScore ||
        a.title.localeCompare(b.title) ||
        a.id.localeCompare(b.id),
    )
    .slice(0, Math.max(0, limit))
    .map((item) => {
      const cta = KIND_CTA[item.kind];
      return {
        item,
        actionLabel: cta.label,
        targetView: cta.view,
        askQuery: `${item.title}. ${item.reason}. What should I do about this?`,
      };
    });
}

// ---------------------------------------------------------------------------
// 2 — Plain-language AQI
// ---------------------------------------------------------------------------

/** The bare score translated into words a first-time viewer understands. */
export interface AqiSummary {
  band: 'strong' | 'steady' | 'slipping';
  /** Plain-language headline, e.g. "Ops health: steady". */
  label: string;
  /** One line of "what this means / what to do", naming the weakest lever. */
  advice: string;
}

const BAND_MEANING: Record<AqiSummary['band'], string> = {
  strong: 'Work is moving — follow-ups, pipeline, tickets and tasks are on track.',
  steady: 'Mostly on track, but some work is starting to slip.',
  slipping: 'Work is stalling — overdue items are piling up.',
};

/** Per-component "do this to raise the score" verb phrase. */
const COMPONENT_FIX: Record<AqiComponent['key'], string> = {
  followups: 'clear the overdue follow-ups',
  pipeline: 'touch your stale prospects',
  tickets: 'unblock the stuck tickets',
  tasks: 'close or reschedule the overdue tasks',
  leverage: 'run the agents and approve their proposals',
};

/**
 * Translate an AQI result into a headline + one actionable line: what the
 * score means, which component drags it most, and the verb that fixes it.
 */
export function summarizeAqi(aqi: AqiResult): AqiSummary {
  const band = aqiBand(aqi.score);
  const label = `Ops health: ${band}`;
  const weakest = [...aqi.components].sort((a, b) => a.score - b.score)[0];
  const advice = weakest
    ? `${BAND_MEANING[band]} Biggest drag: ${weakest.label.toLowerCase()} (${weakest.detail.toLowerCase()}) — ${COMPONENT_FIX[weakest.key]} to raise it.`
    : BAND_MEANING[band];
  return { band, label, advice };
}
